import { useCallback } from "react";

import Account from "../../../../../Account";
import filters from "../../../../../filters";

import type {
  Term,
  GenericTermWithoutComparator,
  GenericTerm,
  CombinedTerm,
  QueryDescription,
} from "./types";

type OrderMap = Record<string, number>;

const orderMap: OrderMap = {
  amount: 1,
  "*": 2,
  balance: 3,
  account: 4,
} as const;

export const parseTerms = (
  query: string,
  accounts: Record<API.AccountId, API.Account> | Account[],
): Term[] | null => {
  if (!query) return [];
  const comparators = [":", ">", "<"];
  const tokens: string[] = query.split(" ").filter(Boolean);

  const terms = tokens
    .map((token) => {
      const tokenSplit = token.split(/[:><]/);

      if (tokenSplit.length === 1) {
        // The token is a generic term without a comparator.
        return {
          discriminator: "genericNoComp",
          key: null,
          value: token,
          comparator: null,
        } satisfies GenericTermWithoutComparator;
      }
      if (tokenSplit.length === 2) {
        // The token is a generic term with a comparator.
        return {
          discriminator: "generic",
          key: tokenSplit[0],
          value: tokenSplit[1],
          comparator: comparators.find((comp) => token.includes(comp))!,
        } satisfies GenericTerm;
      }
      return null;
    })
    .filter(Boolean) as Term[];

  const validatedTerms = terms.filter((term) => {
    // Skip validation for generic terms without a key.
    if (term.key === null) return true;

    if (term.key === "account") {
      // Validate that the account exists.
      return Array.isArray(accounts)
        ? !!accounts.find(({ id }) => id === (term.value as API.AccountId))
        : !!(accounts[term.value as API.AccountId] || false);
    }
    if (term.key === "amount" || term.key === "balance") {
      // Validate that the value is a number.
      return (
        !Number.isNaN(parseFloat(term.value as string)) &&
        Number.isFinite(parseFloat(term.value as string))
      );
    }
    // For other keys, we can assume they are valid.
    return true;
  });

  // If there are no validated terms, return null.
  return validatedTerms.length ? validatedTerms : null;
};

export const useCustomAlertTerms = (
  accounts: Record<API.AccountId, API.Account> | Account[],
) => {
  const combineTerms = useCallback(
    (terms: Term[] | null, keyName: string | null): Term[] | null => {
      if (!terms) return null;

      if (keyName === null) {
        const nullKeyTerms = terms.filter(
          (term) => term.key === null,
        ) as GenericTermWithoutComparator[];

        if (nullKeyTerms.length < 1) return terms;

        const otherTerms: Term[] = terms.filter(
          (term) => term.key !== null,
        ) as Term[];

        return otherTerms.concat([
          {
            discriminator: "genericNoComp",
            key: null,
            value: nullKeyTerms.map((term) => term.value as string),
            comparator: null,
          } satisfies GenericTermWithoutComparator,
        ]) as Term[];
      }

      const lowerBoundTerm: GenericTerm | undefined = terms.find(
        (term) => term.key === keyName && term.comparator === ">",
      ) as GenericTerm | undefined;

      const upperBoundTerm: GenericTerm | undefined = terms.find(
        (term) => term.key === keyName && term.comparator === "<",
      ) as GenericTerm | undefined;

      if (!lowerBoundTerm || !upperBoundTerm) return terms;

      const combinedTerm: CombinedTerm = {
        discriminator: "combined",
        key: keyName,
        value: {
          lower: lowerBoundTerm.value,
          upper: upperBoundTerm.value,
        },
        comparator: null,
      };

      return terms
        .filter((term) => !(term.key === keyName))
        .concat(combinedTerm);
    },
    [],
  );

  const sortTerms = useCallback((terms: Term[] | null): Term[] | null => {
    if (!terms) return null;

    const getOrderKey = (keyName: string): keyof OrderMap => {
      return Object.keys(orderMap).includes(keyName) ? keyName : "*";
    };

    const sortedTerms = [...terms].sort((a, b) => {
      return (
        orderMap[getOrderKey(a.key || "")] - orderMap[getOrderKey(b.key || "")]
      );
    });

    return sortedTerms;
  }, []);

  const formatDollarAmount = useCallback((currencyString: string): string => {
    return filters.currency(+currencyString as Dollars, { hasDecimal: true });
  }, []);

  const getGenericTermString = useCallback(
    (prefix: string, term: GenericTerm): string => {
      switch (term.comparator) {
        case ":":
          return `${prefix}is equal to ${formatDollarAmount(term.value)}`;
        case "<":
          return `${prefix}is less than ${formatDollarAmount(term.value)}`;
        case ">":
          return `${prefix}is greater than ${formatDollarAmount(term.value)}`;
        default:
          return "";
      }
    },
    [formatDollarAmount],
  );

  const termToString = useCallback(
    (term: Term): string => {
      let prefix: string = "";
      switch (term.discriminator) {
        case "combined":
          prefix = term.key === "amount" ? "" : "the resulting balance ";
          return `${prefix}is between ${formatDollarAmount(term.value.lower)} and ${formatDollarAmount(term.value.upper)}`;

        case "generic":
          prefix = term.key === "amount" ? "" : "the resulting balance ";
          return getGenericTermString(prefix, term);

        case "genericNoComp":
          return typeof term.value === "string"
            ? `contains the keyword "${term.value}"`
            : `contains the keyword${term.value.length > 1 ? "s" : ""} ${term.value.map((valueStr) => `"${valueStr}"`).join(", ")}`;
        default:
          return "";
      }
    },
    [formatDollarAmount, getGenericTermString],
  );

  const getAccount = useCallback(
    (id: API.AccountId) => {
      if (Array.isArray(accounts)) {
        return accounts.find(({ id: accountId }) => accountId === id);
      }
      return accounts[id] ? Account.deserialize(accounts[id]) : undefined;
    },
    [accounts],
  );

  const describeQuery = useCallback(
    (initialQuery: string): QueryDescription => {
      let terms = parseTerms(initialQuery, accounts);

      terms = combineTerms(terms, "amount");
      terms = combineTerms(terms, "balance");
      terms = combineTerms(terms, null);
      terms = sortTerms(terms);
      if (!terms) {
        return { valid: false };
      }

      const termsIncludeTransactionKeywords: boolean = !!terms.find(
        (term) => !["balance", "account"].includes(term.key || ""),
      );

      const accountTerm = terms.find((term) => term.key === "account");
      if (accountTerm) terms = terms.filter((term) => term.key !== "account");

      // Create the query description.
      const otherTerms: string[] = terms.map(termToString);

      let commaSeparatedElements: string[] = otherTerms;
      let lastElement: string | null = null;

      if (otherTerms.length > 1) {
        commaSeparatedElements = otherTerms.slice(0, -1);
        lastElement = otherTerms[otherTerms.length - 1];
      }
      const customAlertDescription =
        `when ${termsIncludeTransactionKeywords ? "a transaction " : ""}` +
        `${commaSeparatedElements.join(", ")}` +
        `${lastElement ? ` and ${lastElement}` : ""}`;

      const account = accountTerm
        ? getAccount(accountTerm.value as API.AccountId)
        : null;

      return {
        terms,
        valid: true,
        description: customAlertDescription,
        account: account || null,
      };
    },
    [accounts, combineTerms, getAccount, sortTerms, termToString],
  );

  return { describeQuery };
};
